import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import type { Manifest, ManifestReference, Reference } from '../api/authoring'
import { payloadId, shown } from '../api/useIntent'
import type { LinkPath, PinState } from '../model/link'
import { typeTitle } from '../model/manifest'
import { holds } from '../model/schema'
import {
  acceptsLabel,
  declaredReferences,
  mintEdgeId,
  referenceEdges,
  unaccountedReferences,
} from '../model/references'
import { ComponentName } from './ComponentName'
import { PinChip } from './PinChip'
import { PinDialog } from './PinDialog'
import { ReferencePicker } from './ReferencePicker'

/**
 * What the selected node refers to, and the way to change it.
 *
 * **Offered by the host, not by a type's editor**, on the same terms as
 * the uploader and the pin: a reference is the core's edge, its intents
 * are the core's, and every type that declares one would otherwise grow
 * the same panel. Which types declare what comes off the manifest, so a
 * type registered tomorrow gets this control with no change here.
 *
 * The edges are drawn from the *declaration* rather than from the
 * document, so an edge with nothing on it is a row that says what it
 * takes. That is the state a Pattern placement is created in, and a panel
 * that drew only what exists would show an author an empty box at exactly
 * the moment they need to be told a declaration is missing.
 *
 * **A reference is added, not placed**, and the difference is visible
 * here: there is no position, no reordering and no region. The order
 * references appear in is the order they were added, and nothing reads
 * it.
 */
export function ReferenceEditor({
  tenant,
  manifest,
  typeName,
  references,
  fields,
  commandId,
  commandPath,
  send,
  busy,
}: {
  tenant: string
  manifest: Manifest | undefined
  /** The selected node's type — whose declarations these are. */
  typeName: string
  /** What the node refers to now. */
  references: Reference[]
  /**
   * The node's own fields, which an edge's `when` is read against. An
   * edge the type narrowed to other holders is not offered here: the
   * platform refuses it on this one, and the type's own intent is where
   * it is written (a Link's file goes with its kind).
   */
  fields?: Record<string, unknown>
  /** The component an intent is sent to. */
  commandId: string
  /** The link path to the node within that component. */
  commandPath: LinkPath
  send: (intent: string, payload: Record<string, unknown>) => Promise<unknown>
  busy: boolean
}) {
  const declared = declaredReferences(manifest, typeName).filter((edge) => holds(edge.when, fields ?? {}))
  const [adding, setAdding] = useState<ManifestReference | undefined>()
  const [pinning, setPinning] = useState<Reference | undefined>()

  if (declared.length === 0 && references.length === 0) return null

  const edges = referenceEdges(declared, references)
  const unaccounted = unaccountedReferences(declared, references)
  const taken = new Set(references.map((reference) => reference.edgeId))

  const remove = (reference: Reference) => send('component.reference_removed', { edgeId: reference.edgeId }).catch(shown)

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant='h3'>Connected content</Typography>
        <Typography variant='caption' color='text.secondary'>
          The pattern, content, or collection this block uses to produce what visitors see.
        </Typography>
      </Stack>

      {edges.map((edge) => (
        <Box key={edge.declares.name}>
          <Stack
            direction='row'
            spacing={1}
            alignItems='baseline'
            flexWrap='wrap'
            useFlexGap
          >
            <Typography variant='body2' fontWeight={600}>
              {edgeLabel(edge.declares.name)}
            </Typography>
            {edge.declares.authoredNames && (
              <Tooltip title='A family of edges. The pattern declaration names them; a placement says which record fills each name.'>
                <Chip
                  size='small'
                  variant='outlined'
                  label='named by the declaration'
                />
              </Tooltip>
            )}
            {edge.declares.multiple && <Chip size='small' variant='outlined' label='many' />}
            <Typography variant='caption' color='text.secondary'>
              {acceptsLabel(edge.declares)}
            </Typography>
          </Stack>

          {edge.held.length === 0 && (
            <Typography variant='caption' color='text.secondary'>
              Nothing chosen yet. Until something is, this block shows nothing on the site.
            </Typography>
          )}

          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {edge.held.map((reference) => (
              <Held
                key={reference.edgeId}
                tenant={tenant}
                manifest={manifest}
                reference={reference}
                named={Boolean(edge.declares.authoredNames)}
                busy={busy}
                onPin={() => setPinning(reference)}
                onRemove={() => remove(reference)}
              />
            ))}
          </Stack>

          {edge.open && (
            <Button
              size='small'
              startIcon={<AddIcon />}
              disabled={busy}
              onClick={() => setAdding(edge.declares)}
              sx={{ mt: 0.5 }}
            >
              {edge.held.length === 0 ? `Choose ${edgeLabel(edge.declares.name)}` : `Add another`}
            </Button>
          )}
        </Box>
      ))}

      {unaccounted.length > 0 && (
        <Box>
          <Typography variant='body2' fontWeight={600}>
            Undeclared
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            The type no longer declares these names. They are still stored and still followed.
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {unaccounted.map((reference) => (
              <Stack
                key={reference.edgeId}
                direction='row'
                spacing={1}
                alignItems='center'
              >
                <Typography variant='body2' sx={{ flex: 1 }} noWrap>
                  {reference.name} · {reference.type} · {reference.id}
                </Typography>
                <IconButton
                  size='small'
                  disabled={busy}
                  onClick={() => remove(reference)}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      <ReferencePicker
        tenant={tenant}
        declares={adding}
        open={Boolean(adding)}
        busy={busy}
        onClose={() => setAdding(undefined)}
        onChoose={(chosen) => {
          setAdding(undefined)
          // The target's **type is not stated**: the surface reads it off
          // the component and refuses a claim, the same rule a linked
          // child follows. The id is composite because it travels inside
          // an untyped payload.
          send('component.reference_added', {
            edgeId: mintEdgeId(chosen.name, taken),
            name: chosen.name,
            id: payloadId(tenant, chosen.id),
          }).catch(shown)
        }}
      />

      {pinning && (
        <PinDialog
          tenant={tenant}
          open
          commandId={commandId}
          commandPath={commandPath}
          edge={{ kind: 'reference', edgeId: pinning.edgeId }}
          targetId={pinning.id}
          pin={pinOf(pinning)}
          onClose={() => setPinning(undefined)}
        />
      )}
    </Stack>
  )
}

/**
 * One reference, as the holder shows it.
 *
 * **The chosen component by its name.** The row said `type` and then the
 * target's id, which answers "is something chosen" and nothing else —
 * and the question an author is actually asking of a pattern's
 * declaration or a repeater's collection is *which one*. The id is on
 * the hover, where build mode keeps the rest of the model.
 *
 * A row rather than a fragment in the map above, because naming the
 * target means reading its document and a hook cannot be called in a
 * loop.
 */
function Held({
  tenant,
  manifest,
  reference,
  named,
  busy,
  onPin,
  onRemove,
}: {
  tenant: string
  manifest: Manifest | undefined
  reference: Reference
  /** Whether the edge is a family, whose member names an author wrote. */
  named: boolean
  busy: boolean
  onPin: () => void
  onRemove: () => void
}) {
  return (
    <Stack
      direction='row'
      spacing={1}
      alignItems='center'
      sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant='body2' noWrap>
          {named ? `${reference.name} · ` : ''}
          {typeTitle(manifest, reference.type)}
        </Typography>
        <ComponentName
          tenant={tenant}
          id={reference.id}
          revision={reference.revision}
          manifest={manifest}
        />
      </Stack>
      <PinChip pin={pinOf(reference)} onClick={onPin} />
      <Tooltip title='Disconnect it. What it points at is kept.'>
        <span>
          <IconButton size='small' disabled={busy} onClick={onRemove}>
            <DeleteOutlineIcon fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}

function edgeLabel(name: string): string {
  switch (name) {
    case 'declaration':
      return 'how it looks'
    case 'input':
      return 'content'
    case 'source':
      return 'collection'
    default:
      return name
  }
}

/**
 * A reference's pin state.
 *
 * The same two states a linked child has and never the third: a reference
 * always names a standalone component, so there is no inline case to
 * answer for.
 */
function pinOf(reference: Reference): PinState {
  return reference.revision === undefined ? { kind: 'latest' } : { kind: 'pinned', revision: reference.revision }
}
